// Area zones: dash trails (thin rotated rects), burn / shock / slow circles, gravity fields
// (pull enemies, bend enemy shots, implode with endDamage) and barrier visuals.
// Visuals are a handful of additive images per zone, animated in update() (no Graphics redraws).

import Phaser from 'phaser';
import { TEX } from '../../art/keys';
import { DEPTH } from '../../config/constants';
import { COLORS } from '../../config/palette';
import type { EnemyApi, FxApi, GameContext, ZoneApi, ZoneHandle, ZoneKind, ZoneSpec } from '../../core/contracts';
import type { LoopHandle } from '../../core/contracts';
import type { StatusApplication, Tag, TrailKind } from '../../core/types';
import { TRAIL_DEFAULT_STATUS } from './defaults';
import { destroyObj, enemyList, flag, livePlayer, targetable, type ProjectileSystem, type ZoneSpecEx } from './internal';
import type { CombatSfx } from './sfx';
import { CTEX, ensureCombatTextures, texOr } from './textures';

/** Damage ticks per second inside damaging zones. */
const DAMAGE_TICK = 0.25;
/** Status re-application interval. */
const STATUS_TICK = 0.5;
/** GRAVITY_FURNACE burn interval. */
const FURNACE_TICK = 1;
/** Trail thickness (centred on the body path). */
const TRAIL_HEIGHT = 40;
const FADE_OUT = 0.22;
const MOTES = 8;

const KIND_COLOR: Record<ZoneKind, number> = {
  burn: COLORS.burn,
  shock: COLORS.electric,
  gravity: COLORS.gravity,
  slow: 0x9fd8ff,
  barrier: COLORS.shield,
};

const KIND_TAGS: Record<ZoneKind, Tag[]> = {
  burn: ['burn'],
  shock: ['electric'],
  gravity: ['gravity'],
  slow: [],
  barrier: ['shield'],
};

const TRAIL_ZONE: Record<TrailKind, ZoneKind> = { electric: 'shock', burn: 'burn', plasma: 'burn', void: 'slow' };
const TRAIL_COLOR: Record<TrailKind, number> = { electric: COLORS.electric, burn: COLORS.burn, plasma: 0xff6ae6, void: COLORS.wraith };
const TRAIL_TEX: Record<TrailKind, string> = {
  electric: TEX.trailElectric,
  burn: TEX.trailBurn,
  plasma: TEX.trailPlasma,
  void: TEX.trailVoid,
};
const TRAIL_TAGS: Record<TrailKind, Tag[]> = { electric: ['electric'], burn: ['burn'], plasma: ['dash'], void: [] };

class Zone implements ZoneHandle {
  alive = true;
  remaining: number;
  x: number;
  y: number;
  age = 0;
  dmgT = 0;
  statusT = 0;
  furnaceT = FURNACE_TICK * 0.5;
  fxT = 0;
  /** Seconds left of the fade-out after the zone ended (visuals only). */
  fade = -1;
  imgs: Phaser.GameObjects.Image[] = [];
  motes: Phaser.GameObjects.Image[] = [];
  readonly color: number;

  constructor(
    readonly id: number,
    readonly spec: ZoneSpecEx,
    private readonly onEnd: (z: Zone) => void,
  ) {
    this.remaining = Math.max(0.05, spec.duration);
    this.x = spec.x;
    this.y = spec.y;
    this.color = spec.color ?? (spec.trailKind ? TRAIL_COLOR[spec.trailKind] : KIND_COLOR[spec.kind] ?? COLORS.uiText);
  }

  get isRect(): boolean {
    return this.spec.w !== undefined && this.spec.h !== undefined && this.spec.radius === undefined;
  }

  get radius(): number {
    return this.spec.radius ?? Math.max(this.spec.w ?? 0, this.spec.h ?? 0) * 0.5;
  }

  /** Does a circle (enemy body approximation) at (px, py) with radius pr overlap the zone? */
  contains(px: number, py: number, pr: number): boolean {
    if (this.isRect) {
      const a = this.spec.angle ?? 0;
      const dx = px - this.x;
      const dy = py - this.y;
      const c = Math.cos(-a);
      const s = Math.sin(-a);
      const lx = dx * c - dy * s;
      const ly = dx * s + dy * c;
      return Math.abs(lx) <= (this.spec.w ?? 0) * 0.5 + pr && Math.abs(ly) <= (this.spec.h ?? 0) * 0.5 + pr;
    }
    const r = this.spec.radius ?? 0;
    const dx = px - this.x;
    const dy = py - this.y;
    const rr = r + pr;
    return dx * dx + dy * dy <= rr * rr;
  }

  end(): void {
    if (!this.alive) return;
    this.alive = false;
    this.onEnd(this);
  }
}

export function createZones(ctx: GameContext, fx: FxApi, sfx: CombatSfx, projectiles: () => ProjectileSystem): ZoneApi {
  const scene = ctx.scene;
  const zones: Zone[] = [];
  const fading: Zone[] = [];
  let nextId = 1;
  let hum: LoopHandle | null = null;
  let destroyed = false;
  const inside: EnemyApi[] = [];

  const tex = (key: string, fallback: string) => texOr(scene, key, fallback);

  function buildVisuals(z: Zone): void {
    ensureCombatTextures(scene);
    const add = (key: string, depth: number, blend: number, tint: number, alpha: number): Phaser.GameObjects.Image => {
      const img = scene.add.image(z.x, z.y, key).setDepth(depth).setBlendMode(blend).setTint(tint).setAlpha(alpha);
      z.imgs.push(img);
      return img;
    };
    const ADD = Phaser.BlendModes.ADD;
    const NORMAL = Phaser.BlendModes.NORMAL;
    const s = z.spec;
    if (z.isRect) {
      const w = s.w ?? 48;
      const h = s.h ?? TRAIL_HEIGHT;
      const key = s.trailKind ? tex(TRAIL_TEX[s.trailKind], CTEX.band) : CTEX.band;
      const glow = add(CTEX.soft, DEPTH.ZONES, ADD, z.color, 0.22);
      glow.setDisplaySize(w + h, h * 2.6).setRotation(s.angle ?? 0);
      const band = add(key, DEPTH.ZONES + 1, ADD, z.color, 0.9);
      band.setDisplaySize(w, h * 1.25).setRotation(s.angle ?? 0);
      const core = add(CTEX.band, DEPTH.ZONES + 1, ADD, 0xffffff, 0.35);
      core.setDisplaySize(w * 0.96, h * 0.45).setRotation(s.angle ?? 0);
      return;
    }
    const r = Math.max(8, s.radius ?? 64);
    const ringKey = tex(TEX.ring, CTEX.ring);
    const softKey = tex(TEX.soft, CTEX.soft);
    if (s.kind === 'gravity') {
      // Dark singularity core under the enemies, bright swirl above them.
      const core = add(softKey, DEPTH.ZONES, NORMAL, 0x0d0619, 0.85);
      core.setDisplaySize(r * 1.1, r * 1.1);
      const disc = add(softKey, DEPTH.FX - 1, ADD, z.color, 0.32);
      disc.setDisplaySize(r * 2.3, r * 2.3);
      const ringA = add(ringKey, DEPTH.FX - 1, ADD, z.color, 0.8);
      ringA.setDisplaySize(r * 2, r * 2);
      const ringB = add(ringKey, DEPTH.FX - 1, ADD, 0xe6dcff, 0.6);
      ringB.setDisplaySize(r * 2, r * 2);
      const eye = add(softKey, DEPTH.FX, ADD, 0xf2ecff, 0.9);
      eye.setDisplaySize(r * 0.28, r * 0.28);
      for (let i = 0; i < MOTES; i++) {
        const m = scene.add.image(z.x, z.y, CTEX.dot).setDepth(DEPTH.FX).setBlendMode(ADD).setTint(i % 2 ? 0xffffff : z.color);
        m.setScale(1.2 + (i % 3) * 0.5);
        z.motes.push(m);
      }
      return;
    }
    const disc = add(softKey, DEPTH.ZONES, ADD, z.color, s.kind === 'barrier' ? 0.22 : 0.3);
    disc.setDisplaySize(r * 2.4, r * 2.4);
    const ring = add(ringKey, DEPTH.ZONES + 1, ADD, z.color, 0.85);
    ring.setDisplaySize(r * 2, r * 2);
    if (s.kind === 'barrier' && scene.textures.exists(TEX.shield)) {
      const hex = add(TEX.shield, DEPTH.FX - 1, ADD, z.color, 0.5);
      hex.setDisplaySize(r * 2, r * 2);
    }
  }

  function animate(z: Zone, dt: number): void {
    const s = z.spec;
    const life = Math.max(0.05, s.duration);
    // Fade in quickly, fade out over the last 25% (or the post-end fade).
    let a = Math.min(1, z.age / 0.12);
    if (z.fade >= 0) a *= Math.max(0, z.fade / FADE_OUT);
    else a *= Math.min(1, z.remaining / Math.min(0.6, life * 0.25));
    const flicker = 0.88 + 0.12 * Math.sin(z.age * 23 + z.id);
    if (z.isRect) {
      const [glow, band, core] = z.imgs;
      glow?.setPosition(z.x, z.y).setAlpha(0.22 * a);
      band?.setPosition(z.x, z.y).setAlpha(0.9 * a * flicker);
      core?.setPosition(z.x, z.y).setAlpha(0.35 * a * flicker);
      return;
    }
    const r = Math.max(8, s.radius ?? 64);
    if (s.kind === 'gravity') {
      const [core, disc, ringA, ringB, eye] = z.imgs;
      const pulse = 1 + 0.06 * Math.sin(z.age * 9);
      core?.setPosition(z.x, z.y).setAlpha(0.85 * a).setDisplaySize(r * 1.1 * pulse, r * 1.1 * pulse);
      disc?.setPosition(z.x, z.y).setAlpha(0.32 * a);
      // Two rings contracting toward the centre, half a cycle apart: reads as "sucking in".
      const cyc = 0.7;
      for (let k = 0; k < 2; k++) {
        const ring = k === 0 ? ringA : ringB;
        if (!ring) continue;
        const t = ((z.age / cyc + k * 0.5) % 1 + 1) % 1;
        const rr = r * 2 * (1 - 0.75 * t);
        ring.setPosition(z.x, z.y).setDisplaySize(rr, rr).setAlpha(a * (k === 0 ? 0.8 : 0.6) * Math.sin(t * Math.PI));
        ring.rotation += dt * (k === 0 ? 2.2 : -1.6);
      }
      eye?.setPosition(z.x, z.y).setAlpha(0.9 * a * flicker);
      for (let i = 0; i < z.motes.length; i++) {
        const m = z.motes[i];
        const t = ((z.age * 0.9 + i / z.motes.length) % 1 + 1) % 1;
        const ang = i * ((Math.PI * 2) / z.motes.length) + z.age * 3.4 + t * 2.5;
        const d = r * (1 - t) * 0.95;
        m.setPosition(z.x + Math.cos(ang) * d, z.y + Math.sin(ang) * d).setAlpha(a * Math.min(1, t * 3) * (1 - t * 0.4));
      }
      return;
    }
    const [disc, ring, hex] = z.imgs;
    disc?.setPosition(z.x, z.y).setAlpha((s.kind === 'barrier' ? 0.22 : 0.3) * a * flicker);
    if (ring) {
      const breathe = 1 + 0.035 * Math.sin(z.age * 5);
      ring.setPosition(z.x, z.y).setDisplaySize(r * 2 * breathe, r * 2 * breathe).setAlpha(0.85 * a);
      ring.rotation += dt * 0.8;
    }
    hex?.setPosition(z.x, z.y).setAlpha(0.5 * a);
  }

  /** Periodic particles along the zone so damaging zones read as dangerous. */
  function ambientFx(z: Zone, dt: number): void {
    z.fxT -= dt;
    if (z.fxT > 0) return;
    const s = z.spec;
    if (z.isRect) {
      z.fxT = 0.12;
      const w = s.w ?? 48;
      const a = s.angle ?? 0;
      const t = Math.random() - 0.5;
      const px = z.x + Math.cos(a) * w * t;
      const py = z.y + Math.sin(a) * w * t;
      if (s.kind === 'burn') fx.embers(px, py, { count: 2, color: z.color });
      else if (s.kind === 'shock') {
        const half = 26;
        fx.lightning(
          [
            { x: px - Math.cos(a) * half, y: py - Math.sin(a) * half + (Math.random() - 0.5) * 14 },
            { x: px + Math.cos(a) * half, y: py + Math.sin(a) * half + (Math.random() - 0.5) * 14 },
          ],
          { color: z.color, width: 2, duration: 90, jitter: 10 },
        );
      } else fx.sparks(px, py, -Math.PI / 2, { count: 1, color: z.color, speed: 60, spread: 1, life: 260 });
      return;
    }
    if (s.kind === 'burn') {
      z.fxT = 0.1;
      const ang = Math.random() * Math.PI * 2;
      const d = Math.sqrt(Math.random()) * (s.radius ?? 64);
      fx.embers(z.x + Math.cos(ang) * d, z.y + Math.sin(ang) * d, { count: 2, color: z.color });
    } else if (s.kind === 'shock') {
      z.fxT = 0.18;
      const ang = Math.random() * Math.PI * 2;
      const r = s.radius ?? 64;
      fx.lightning(
        [
          { x: z.x, y: z.y },
          { x: z.x + Math.cos(ang) * r, y: z.y + Math.sin(ang) * r },
        ],
        { color: z.color, width: 2, duration: 100, jitter: 14 },
      );
    } else {
      z.fxT = 1;
    }
  }

  function enemiesInside(z: Zone): EnemyApi[] {
    inside.length = 0;
    for (const e of enemyList(ctx)) {
      if (!targetable(e) && !(e.alive && !e.spawning && e.kind === 'prop')) continue;
      if (z.contains(e.x, e.y, e.radius * 0.6)) inside.push(e);
    }
    return inside;
  }

  function damageTick(z: Zone): void {
    const s = z.spec;
    const dps = s.damagePerSec ?? 0;
    if (dps <= 0) return;
    const tags = s.tags ?? (s.trailKind ? TRAIL_TAGS[s.trailKind] : KIND_TAGS[s.kind]);
    if (s.owner === 'player') {
      for (const e of enemiesInside(z)) {
        ctx.damage.hitEnemy(e, {
          amount: dps * DAMAGE_TICK,
          tags: tags.slice(),
          source: 'player',
          isDot: true,
          noTrigger: true,
          sourceX: z.x,
          sourceY: z.y,
          label: s.trailKind ? `trail_${s.trailKind}` : `zone_${s.kind}`,
        });
      }
    } else {
      const p = livePlayer(ctx);
      if (p && p.alive && z.contains(p.x, p.y, 20)) {
        ctx.damage.hitPlayer({ amount: dps * STATUS_TICK, tags: tags.slice(), source: 'enemy', sourceX: z.x, sourceY: z.y, label: `zone_${s.kind}` });
      }
    }
  }

  function statusTick(z: Zone): void {
    const s = z.spec;
    if (s.owner !== 'player') return;
    let st: StatusApplication | undefined = s.status;
    if (!st && s.kind === 'slow') st = { id: 'slow', duration: 0.6, potency: 0.35 };
    if (!st) return;
    for (const e of enemiesInside(z)) ctx.status.apply(e, st);
  }

  function gravityPull(z: Zone, dt: number): void {
    const s = z.spec;
    const r = Math.max(8, s.radius ?? 120);
    const strength = s.strength ?? 1200;
    if (s.owner === 'player') {
      for (const e of enemyList(ctx)) {
        if (!e.alive || e.spawning || e.kind === 'prop') continue;
        const dx = z.x - e.x;
        const dy = z.y - e.y;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d > r + e.radius * 0.5 || d < 0.001) continue;
        // Stronger toward the centre, eased to zero in the last 24 px so targets settle instead of jittering.
        let acc = strength * (0.4 + 0.6 * (1 - Math.min(1, d / r)));
        if (d < 24) acc *= d / 24;
        acc *= 1 - Math.min(0.85, e.knockbackResist * 0.7) - (e.kind === 'boss' ? 0.6 : 0);
        if (acc <= 0) continue;
        e.pull((dx / d) * acc, (dy / d) * acc, dt);
      }
      projectiles().nudgeEnemyShots(z.x, z.y, r * 1.15, strength * 0.45, dt);
    }
  }

  function finish(z: Zone): void {
    const s = z.spec;
    if (s.kind === 'gravity' && (s.endDamage ?? 0) > 0) {
      const r = Math.max(8, s.radius ?? 120);
      fx.ring(z.x, z.y, 8, { color: COLORS.gravity, duration: 200, width: 6, fromRadius: r });
      try {
        ctx.damage.explode(
          z.x,
          z.y,
          r * 0.8,
          {
            amount: s.endDamage ?? 0,
            tags: ['gravity', 'explosive'],
            source: s.owner === 'player' ? 'player' : 'enemy',
            knockback: 320,
            label: 'implosion',
            noTrigger: s.owner === 'player' ? undefined : true,
          },
          { color: COLORS.gravity, hurtsPlayer: s.owner === 'enemy' },
        );
      } catch (err) {
        console.warn('[combat] implosion failed', err);
      }
      sfx.play('gravity_implode', { volume: 0.9, x: z.x }, 50);
    }
    z.fade = FADE_OUT;
    fading.push(z);
    const i = zones.indexOf(z);
    if (i >= 0) zones.splice(i, 1);
    updateHum();
  }

  function updateHum(): void {
    const any = zones.some((z) => z.spec.kind === 'gravity');
    if (any && !hum && !destroyed) hum = sfx.loop('field_hum', { volume: 0.45 });
    else if (!any && hum) {
      hum.stop(250);
      hum = null;
    }
  }

  function destroyZone(z: Zone): void {
    for (const img of z.imgs) destroyObj(img);
    for (const m of z.motes) destroyObj(m);
    z.imgs.length = 0;
    z.motes.length = 0;
  }

  const api: ZoneApi = {
    add(spec: ZoneSpec): ZoneHandle {
      const z = new Zone(nextId++, { ...spec } as ZoneSpecEx, finish);
      if (destroyed) {
        z.alive = false;
        return z;
      }
      zones.push(z);
      try {
        buildVisuals(z);
      } catch (err) {
        console.warn('[combat] zone visuals failed', err);
      }
      if (spec.kind === 'gravity') {
        sfx.play('gravity_field', { volume: 0.8, x: spec.x }, 60);
        fx.ring(spec.x, spec.y, spec.radius ?? 120, { color: COLORS.gravity, duration: 260, width: 4, fromRadius: 12 });
        updateHum();
      }
      return z;
    },

    trail(kind, x0, y0, x1, y1, opts) {
      let dx = x1 - x0;
      let dy = y1 - y0;
      let len = Math.sqrt(dx * dx + dy * dy);
      if (len < 24) {
        // Dash cancelled almost immediately: a short horizontal patch where it started.
        dx = 1;
        dy = 0;
        len = 48;
        x1 = x0 + 24;
        x0 -= 24;
      }
      const spec: ZoneSpecEx = {
        kind: TRAIL_ZONE[kind] ?? 'burn',
        owner: 'player',
        x: (x0 + x1) / 2,
        y: (y0 + y1) / 2,
        w: len + 24,
        h: TRAIL_HEIGHT,
        duration: Math.max(0.2, opts.duration),
        damagePerSec: opts.dps,
        status: opts.status ?? TRAIL_DEFAULT_STATUS[kind],
        tags: TRAIL_TAGS[kind].slice(),
        color: TRAIL_COLOR[kind],
        angle: Math.atan2(dy, dx),
        trailKind: kind,
      };
      api.add(spec);
    },

    get list(): readonly ZoneHandle[] {
      return zones;
    },

    update(dt) {
      for (let i = zones.length - 1; i >= 0; i--) {
        const z = zones[i];
        if (!z) continue;
        z.age += dt;
        z.remaining -= dt;
        const s = z.spec;
        try {
          if (s.kind === 'gravity') {
            gravityPull(z, dt);
            if (s.owner === 'player' && flag(ctx, 'GRAVITY_FURNACE')) {
              z.furnaceT -= dt;
              if (z.furnaceT <= 0) {
                z.furnaceT += FURNACE_TICK;
                for (const e of enemiesInside(z)) {
                  ctx.status.apply(e, { id: 'burn', stacks: 1, duration: 3, potency: 6 });
                  fx.embers(e.x, e.y, { count: 4, color: COLORS.burn });
                }
              }
            }
          }
          if (s.kind !== 'barrier') {
            z.dmgT -= dt;
            if (z.dmgT <= 0) {
              z.dmgT += s.owner === 'player' ? DAMAGE_TICK : STATUS_TICK;
              damageTick(z);
            }
            z.statusT -= dt;
            if (z.statusT <= 0) {
              z.statusT += STATUS_TICK;
              statusTick(z);
            }
          }
          ambientFx(z, dt);
        } catch (err) {
          console.warn('[combat] zone update failed', err);
        }
        animate(z, dt);
        if (z.alive && z.remaining <= 0) z.end();
      }
      for (let i = fading.length - 1; i >= 0; i--) {
        const z = fading[i];
        z.fade -= dt;
        z.age += dt;
        if (z.fade <= 0) {
          destroyZone(z);
          fading.splice(i, 1);
        } else animate(z, dt);
      }
    },

    destroy() {
      if (destroyed) return;
      destroyed = true;
      for (const z of zones) {
        z.alive = false;
        destroyZone(z);
      }
      for (const z of fading) destroyZone(z);
      zones.length = 0;
      fading.length = 0;
      if (hum) {
        try {
          hum.stop(0);
        } catch {
          // audio gone
        }
        hum = null;
      }
    },
  };
  return api;
}
